import React, { useEffect, useState, useMemo } from 'react';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import { FormikTextField } from '../../../common/formik-material-ui';
import { Formik } from 'formik';
import { MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RecordNow } from '../recorder/RecordNow';
import Axios from '../../../common/axiosInterceptor';
import { user } from '../../../recoil/atoms/user';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { ModalWithStyles } from './ModalWithStyles';
import { agentUserId } from '../../../recoil/atoms/agentUserId';
import * as Yup from 'yup';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useMutation } from 'react-query';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { isUrl } from '../../../common/validators';

const useStyles = makeStyles((theme) => ({
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  spinner: {
    position: 'fixed !important',
  },
}));
export function EditVmDropModal({
  show,
  onHide,
  setRefetchToggleForVmDrop,
  vmName,
  vmId,
  mp3File,
  mp3Ref,
}) {
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const currentUser = useRecoilValueLoadable(agentUserId);

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();

  const [filepaths, setFilepaths] = useState({
    voicemail: isUrl(mp3File) ? mp3File : '',
  });
  const [filenames, setFilenames] = useState({
    voicemail: isUrl(mp3File) ? mp3File.split('/').slice(-1).pop() : '',
  });
  const editVmDropSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Required'),
        editVmMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
      }),
    []
  );
  const [uploadedFiles, setUploadedFiles] = useState({});
  const onChangeFile = (type) => ({ target: { files } }) => {
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };
  const handleRecording = (type) => (files) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };
  useEffect(() => {
    setFilenames({
      voicemail: isUrl(mp3File) ? mp3File.split('/').slice(-1).pop() : '', // filename from uri/path
    });
    setFilepaths({
      voicemail: isUrl(mp3File) ? mp3File : '',
    });
    //   replave with settings seelctor
  }, [vmName]);
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  return (
    <Formik
      onSubmit={async (values, actions) => {
        if (uploadedFiles.voicemail) {
          const data = new FormData();
          data.append('webm', uploadedFiles.voicemail[0]);
          data.append('businessid', userAtom.contents.businessid);
          data.append('apikey', userAtom.contents.apikey);
          data.append('userid', currentUser.contents.id);
          data.append('call', 'HandleAudioRecording');
          data.append('id', currentUser.contents.id);
          data.append('iid', vmId);
          data.append('name', values.name);
          data.append('type', 'agent-voicemail-drop');
          const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          await Axios.post(url, data);
        } else {
          // only updates the name if no file was uploaded
          // const data = new FormData();
          // data.append('call', 'updateVMDropName');
          // data.append('id', vmId);
          // data.append('name', values.name);
          // data.append('apiKey', userAtom.contents.apikey);
          // data.append('userid', currentUser.contents.id);
          // const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
          // await Axios.post(url, data);
          const data = {
            call: methodNameEnum.UPDATE_VM_DROP_NAME,
            apikey: userAtom.contents?.apikey,
            businessid: userAtom.contents?.businessid,
            id: vmId,
            name: values.name,
            userid: currentUser.contents.id,
          };
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.UPDATE_VM_DROP_NAME,
            payload: data,
          });
          if (response?.data?.success) {
            enqueueSnackbarWithStyles({
              message: `Successfully Added ${values.name} VM Drop`,
              alertProps: { severity: 'success' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error editing VM Drop',
              alertProps: { severity: 'error' },
            });
          }
        }
        onHide();
        setRefetchToggleForVmDrop({});
        actions.resetForm({});
      }}
      enableReinitialize={true}
      validationSchema={editVmDropSchema}
      initialValues={{
        name: vmName,
        mp3Type: 'mp3',
        mp3File: '',
        editVmMp3File: {
          name: mp3Ref.current ? mp3Ref.current.split('/').slice(-1).pop() : '',
        },
      }}
    >
      {(formik) => (
        <>
          <form
            className="form form-label-right"
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <ModalWithStyles
              onSubmit={formik.handleSubmit}
              title="Edit VM Drop Message"
              aria-labelledby="example-modal-sizes-title-lg"
              show={show}
              onHide={onHide}
              isSubmitting={formik.isSubmitting}
              disableSubmit={!formik.dirty}
              size="sm"
            >
              <main className="col">
                <div>
                  <FormikTextField
                    name="name"
                    className={classes.field}
                    formik={formik}
                    fullWidth
                    label="Name"
                    size="small"
                    required
                    variant="outlined"
                  />
                </div>
                <div>
                  <RecordNow
                    dropdownLabel="Voice Mail Message"
                    dropdownName="mp3Type"
                    dropdownType={formik.values.mp3Type}
                    outlineLabel="Voice Mail Message"
                    ttsLabel="Voicemail"
                    // ttsName="mp3File"
                    filenameName="mp3File"
                    filesizeName="editVmMp3File.name"
                    formik={formik}
                    filename={filenames.voicemail}
                    filepath={filepaths.voicemail}
                    onChangeFile={onChangeFile('voicemail')}
                    onRecording={handleRecording('voicemail')}
                  >
                    <MenuItem value="mp3">MP3</MenuItem>
                    <MenuItem value="record">Record Now</MenuItem>
                  </RecordNow>
                </div>
              </main>
            </ModalWithStyles>
          </form>
        </>
      )}
    </Formik>
  );
}
